import React, { useState } from "react";
import NumberFormat from "react-number-format";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IconButton, MenuItem, Popover, Stack, TextField, Typography } from "@mui/material";
import { CalculateMeasure, CalculateMeasureGroup } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealActions } from "store";

import { useGroupsForm } from "./GroupsFormContext";

interface Props {
  currencySymbol?: string;
  isAllowedNegative: boolean;
  roundId: number;
  selectedInvestorsIds: number[];
  group_id: number;
}

export const TotalValueForGroup: React.FC<Props> = ({
  currencySymbol,
  isAllowedNegative,
  roundId,
  selectedInvestorsIds,
  group_id
}) => {
  const [totalGroupValue, setTotalGroupValue] = useState<string>("");
  const [splitGroupAnchor, setSplitGroupAnchor] = useState<HTMLButtonElement | null>(null);
  const { updateGroupMeasureData } = useGroupsForm();
  const { getGroupedMeasureDataCalcAction } = useDealActions();

  const handleSplitToEntire = async () => {
    setSplitGroupAnchor(null);
    const { round_id, investors } = await getGroupedMeasureDataCalcAction({
      round_id: roundId,
      split_type: "entire_commitment",
      investor_ids: selectedInvestorsIds,
      value: String(totalGroupValue)
    });

    updateGroupMeasureData(round_id, investors, group_id);
  };

  const handleSplitToGroup = async () => {
    setSplitGroupAnchor(null);
    const { round_id, investors } = await getGroupedMeasureDataCalcAction({
      round_id: roundId,
      split_type: "group_commitment",
      value: String(totalGroupValue),
      investor_ids: selectedInvestorsIds
    });

    updateGroupMeasureData(round_id, investors, group_id);
  };

  return (
    <Stack direction="row" alignItems="center" gap="10px" onClick={(e) => e.stopPropagation()} sx={{ flexGrow: 1 }}>
      <Typography sx={{ paddingLeft: "35px", width: "228px" }}>Total value for selected groups:</Typography>
      <NumberFormat
        thousandSeparator
        isNumericString
        variant="outlined"
        prefix={currencySymbol}
        decimalScale={2}
        label="Value"
        placeholder="Add value"
        sx={{
          maxWidth: "160px",
          ".MuiInputBase-root": {
            pr: 0,
            overflow: "hidden"
          },
          ".MuiInputBase-input": {
            padding: "9.5px 8px"
          },
          ".MuiButtonBase-root": {
            opacity: splitGroupAnchor ? 1 : 0,
            transition: "opacity 0.2s"
          },
          "&:hover": {
            ".MuiButtonBase-root": {
              opacity: 1
            }
          }
        }}
        allowNegative={isAllowedNegative}
        InputProps={{
          endAdornment: (
            <MaterialTooltip isShow title="Split the value">
              <IconButton
                disabled={!totalGroupValue}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setSplitGroupAnchor(e.currentTarget);
                }}
                sx={{
                  "&.Mui-disabled": {
                    background: "#eaeaea",
                    color: "#c0c0c0"
                  },
                  height: "42px",
                  width: "50px",
                  bgcolor: "#0A3142",
                  borderRadius: 0,

                  ":hover": {
                    bgcolor: "#0A3142"
                  }
                }}
              >
                <CalculateMeasure />
                <ArrowDropDownIcon
                  sx={{
                    fontSize: "18px",
                    color: "#fff",
                    transform: splitGroupAnchor ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out"
                  }}
                />
              </IconButton>
            </MaterialTooltip>
          )
        }}
        customInput={TextField}
        value={totalGroupValue}
        onValueChange={({ value }) => setTotalGroupValue(value)}
      />
      <Popover
        open={Boolean(splitGroupAnchor)}
        anchorEl={splitGroupAnchor}
        onClose={() => setSplitGroupAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              mt: "4px"
            }
          }
        }}
      >
        <Stack sx={{ py: "8px" }}>
          <MenuItem onClick={handleSplitToEntire} sx={{ gap: "12px", py: "10px", px: "16px" }}>
            <CalculateMeasure sx={{ "& path": { fill: "#1C1B1F" } }} />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split to entire commitment</Typography>
          </MenuItem>
          <MenuItem onClick={handleSplitToGroup} sx={{ gap: "12px", py: "10px", px: "16px" }}>
            <CalculateMeasureGroup />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split to group commitment</Typography>
          </MenuItem>
        </Stack>
      </Popover>
    </Stack>
  );
};
